import heartIcon from "@/assets/icons/heart-icon.webp";
import CustomTooltip from "@/components/CustomTooltip";
import { getUnicornTotalFunding } from "@/services/api/UnicornTracker";
import { Info } from "@phosphor-icons/react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";

export default function TotalFunding({
  selectedCountryName,
  selectedRegionName,
}) {
  const [totalFunding, setTotalFunding] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unicornCount, setUnicornCount] = useState(0);

  const searchParams = useSearchParams();
  const selectedRegionId = searchParams.get("unicorn_regions");
  const selectedCountryId = searchParams.get("country");
  const sectors = searchParams.get("sectors");
  const stages = searchParams.get("stages");
  const valuationCondition = searchParams.get("valuation_condition");
  const valuationAmount = searchParams.get("valuation_amount");
  const yearFoundedCondition = searchParams.get("year_founded_condition");
  const yearFoundedYear = searchParams.get("year_founded_year");
  const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
  const timeToUnicornMax = searchParams.get("max_time_to_unicorn");
  const unicornStatus = searchParams.get("unicorn_status");
  // Fetch total funding data using the new dedicated API endpoint
  useEffect(() => {
    async function fetchTotalFunding() {
      try {
        setLoading(true);

        const params = { is_unicorn: true };

        const statuses = unicornStatus
          ? unicornStatus.split(",").filter(Boolean)
          : ["current", "fallen", "alumni"];
        params.unicorn_status = statuses.join(",");
        if (statuses.includes("fallen")) params.include_lost_unicorn = 1;
        if (statuses.includes("alumni")) params.include_alumni = 1;
        if (!statuses.includes("current")) params.exclude_current_unicorn = 1;

        if (selectedRegionId) params.unicorn_regions = selectedRegionId;
        if (selectedCountryId) params.countries = selectedCountryId;

        if (sectors) params.sectors = sectors;
        if (stages) params.stages = stages;

        if (valuationCondition && valuationAmount) {
          params.valuation_condition = valuationCondition;
          params.valuation_amount = valuationAmount;
        }

        if (yearFoundedCondition && yearFoundedYear) {
          params.year_founded_condition = yearFoundedCondition;
          params.year_founded_year = yearFoundedYear;
        }

        if (timeToUnicornMin) params.min_time_to_unicorn = timeToUnicornMin;
        if (timeToUnicornMax) params.max_time_to_unicorn = timeToUnicornMax;

        const response = await getUnicornTotalFunding({ params });
        const fundingData = response.data?.data || {};

        setTotalFunding(fundingData.total_funding || 0);
        setUnicornCount(fundingData.unicorn_count || 0);
      } catch (error) {
        console.error("Error fetching total funding:", error);
        setTotalFunding(0);
        setUnicornCount(0);
      } finally {
        setLoading(false);
      }
    }

    fetchTotalFunding();
  }, [
    selectedRegionId,
    selectedCountryId,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    unicornStatus,
  ]);

  const locationLabel = "Total Funding Raised";

  // --- No-data rules ---
  // If unicorns exist but funding sum is 0 => treat as "unknown for all"
  // (Assumption based on your API shape; best is for API to send a coverage field)
  const hasUnicorns = unicornCount > 0;
  const hasFundingData = totalFunding > 0;
  const showNA = !loading && hasUnicorns && !hasFundingData;

  const formatFundingUSDCompact = (value) => {
    if (!value || value === 0) return "0";

    const abs = Math.abs(value);

    if (abs >= 1_000_000_000) {
      const billions = value / 1_000_000_000;
      // sensible rounding
      if (Math.abs(billions) >= 100) return `$${billions.toFixed(0)}B`;
      if (Math.abs(billions) >= 10) return `$${billions.toFixed(0)}B`;
      return `$${billions.toFixed(1)}B`;
    }

    const millions = value / 1_000_000;
    if (Math.abs(millions) >= 100) return `$${millions.toFixed(0)}M`;
    if (Math.abs(millions) >= 10) return `$${millions.toFixed(0)}M`;
    return `$${millions.toFixed(1)}M`;
  };

  return (
    <>
      <div className='flex items-center justify-between gap-1.5'>
        <p className='text-base font-medium sm:text-sm'>{locationLabel}</p>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content='Total capital raised by the companies matching your filters, summed across their lifetimes. Includes equity rounds, debt, secondaries, grants, and post-IPO raises where applicable. Based on current HQ.'
        />
      </div>

      <div className='flex items-center gap-3 mt-2.5'>
        <div className='relative size-9'>
          <Image src={heartIcon.src} layout='fill' alt='Total Funding' />
        </div>
        <span className='text-3xl font-bold'>
          {loading ? (
            <span className='inline-block w-20 h-8 rounded bg-white/10 animate-pulse' />
          ) : showNA ? (
            "N/A"
          ) : (
            formatFundingUSDCompact(totalFunding)
          )}
        </span>
      </div>

      {!loading && showNA && (
        <p className='mt-1 text-xs opacity-40'>Funding data not available.</p>
      )}
    </>
  );
}
